import { IConfig } from '@qunhe/ktracker-types';
import { DefTypes } from '../constants';
import ResourceTimingCompression from '../lib/resourcetiming/compression';
import { ImageCache, ImageQueue } from '../storage/image';
import TimingQueue from '../storage/timing';
import Ajax from '../utils/ajax';
import debounce from '../utils/debounce';
import {
  assignDefaultData,
  formatUrl,
  getDeviceToken, getType,
  isEmptyObj,
} from '../utils/func';
import logger from '../utils/logger';
import SystemOptions from '../utils/options';

/**
 *
 * @param url
 * @param options
 * @private
 */
function _sendImageResourceData(url: string, options: IConfig) {
  const targetSrc = formatUrl(url);
  if (window.performance.getEntriesByName(targetSrc) && !ImageCache.getInstance().includes(targetSrc)) {
    if (!ImageQueue.getInstance().includes(targetSrc)) {
      TimingQueue.getInstance().push(window.performance.getEntriesByName(targetSrc)[0]);

      const sendImageData = debounce(async () => {
        const resource = ResourceTimingCompression.compressResourceTiming(
          window,
          TimingQueue.getInstance().list(),
        );
        const isValidResource =
          (getType(resource) === DefTypes.ARRAY && resource.length > 0) ||
          (getType(resource) === DefTypes.OBJECT && !isEmptyObj(resource)) ||
          false;

        if (isValidResource && TimingQueue.getInstance().size() > 0) {
          const imageData = {
            resource,
            browserId: getDeviceToken(options.deviceTokenKey),
            visitId: SystemOptions.getInstance().getVisitId(),
          };
          await Ajax.send(imageData, options.performanceApiUrl);
        }

        // clear image queue, add to cache
        ImageCache.getInstance().addAll(ImageQueue.getInstance().list());
        ImageQueue.getInstance().clear();
        TimingQueue.getInstance().clear();
      }, options.debounceThreshold);

      sendImageData();
    }
  }
}

async function sendErrorData(
  data: any,
  options: IConfig,
  exceptionApiUrl: string,
  labels?: { [key: string]: string },
  level?: string,
) {
  if (SystemOptions.getInstance().getCurrentLogCount() >= options.maxLogCount || !data || isEmptyObj(data)) {
    return false;
  }

  try {
    await Ajax.send(assignDefaultData(data, options, labels, level), exceptionApiUrl);
    SystemOptions.getInstance().increaseCurrentLogCount();
  } catch (e) {
    console.error(e);
  }
}

export async function processErrorData(data: any, options: IConfig) {
  await sendErrorData(data, options, options.exceptionApiUrl);
}

export async function processErrorDataV2(
  data: any,
  options: IConfig,
  labels: { [key: string]: string; },
  level?: string,
) {
  await sendErrorData(data, options, options.newExceptionApiUrl, labels, level);
}

export function processImageData(
  nodeItem: Node,
  src: string,
  options: IConfig,
  captureError: boolean = false,
) {
  const config = SystemOptions.getInstance().getOptions();

  if (config.debug) {
    logger.info('image[' + src + '] load event');
  }
  nodeItem.addEventListener('load', function loadImage() {
    _sendImageResourceData(src, options);
    nodeItem.removeEventListener('load', loadImage);
  }, false);

  if (captureError) {
    nodeItem.addEventListener('error', async function loadImageError() {
      if (config.debug) {
        logger.info('image[' + src + '] error event');
      }
      // TODO Image 错误日志分级和聚合
      await processErrorData(
        {
          message: `${formatUrl(src)} Load Error`,
          name: 'IMGLoadError',
          url: window.location.href,
        },
        options,
      );
      nodeItem.removeEventListener('error', loadImageError);
    }, false);
  }
}
